import type { EvidenceType } from '@et/schema';
import type { LexiconTone } from './tags.js';

/**
 * Os doze tipos de evidência, agrupados nas quatro famílias de testemunho.
 *
 * A cor passa a dizer alguma coisa: **que espécie de coisa está a testemunhar**
 * — um objecto que sobreviveu, uma cópia de um texto, alguém que escreveu
 * sobre o assunto, ou um registo produzido por uma administração. Antes era
 * tudo verde, e um filtro de doze itens todos da mesma cor não é uma escolha
 * de paleta: é uma dimensão inteira que a página tinha e não mostrava.
 *
 * Quatro e não doze de propósito. Doze cores distintas seriam confetti, e
 * ainda por cima obrigariam a doze pares de contraste novos; estas quatro já
 * são as do sistema e já passam AA em todas as superfícies nos dois temas.
 *
 * A cor nunca anda sozinha — o glifo de `EvidenceGlyph` continua a distinguir
 * os doze tipos por geometria, que é o que sobrevive a um daltonismo e a uma
 * impressão a preto e branco.
 */
export const WITNESS_FAMILY: Record<EvidenceType, LexiconTone> = {
  // o que sobreviveu, e se pode tocar
  archaeology: 'evidence',
  epigraphy: 'evidence',
  numismatics: 'evidence',
  material_culture: 'evidence',
  // o texto, e as cópias por onde chegou
  manuscript: 'source',
  textual_criticism: 'source',
  linguistic: 'source',
  // quem escreveu sobre o assunto
  primary_literary: 'claim',
  external_literary: 'claim',
  // o que uma administração registou por outra razão qualquer
  legal_administrative: 'objection',
  historical_event: 'objection',
  medical: 'objection',
};

export const witnessFamily = (type: string | undefined): LexiconTone =>
  (type === undefined ? 'neutral' : WITNESS_FAMILY[type as EvidenceType] ?? 'neutral');

/**
 * As quatro familias pela ordem em que se leem, e a chave de traducao de cada.
 *
 * Vive aqui, e nao dentro do grafico, porque a lista ao lado passou a usar as
 * mesmas seccoes: se as duas colunas nao concordarem na ordem e nas palavras,
 * deixam de ser duas vistas da mesma coisa e passam a ser duas listas.
 *
 * `neutral` fecha a lista para apanhar um tipo que ainda nao tenha familia —
 * um tipo novo no esquema aparece no fim em vez de desaparecer do ecra.
 */
export const WITNESS_LANES: readonly { readonly family: LexiconTone; readonly key: string }[] = [
  { family: 'evidence', key: 'objects' },
  { family: 'source', key: 'text' },
  { family: 'claim', key: 'testimony' },
  { family: 'objection', key: 'records' },
  { family: 'neutral', key: 'other' },
];
